export type TimerStep = {
  name: string;
  timestamp: number;
  type: 'step' | 'start' | 'finish';
};
export type Timer = TimerStep[];
export type TimerResult = Record<string, number> & { total: number };

/**
 * Start and finish steps default labels
 */
const START_STEP = 'start';
const FINISH_STEP = 'finish';

/**
 * Our stored timers
 */
const timers: Record<string, Timer | undefined> = {};

const byName = (stepName: string) => (step: TimerStep) =>
  step.name === stepName;

/**
 * Initialize a timer, method you have to call to start a timer.
 */
export const start = (
  timerName: string,
  stepName = START_STEP,
  startTimestamp = Date.now(),
): void => {
  timers[timerName] = [];

  addStep(timers[timerName] as Timer, stepName, startTimestamp, 'start');
};

/**
 * Internal method to get existing timer.
 * If timer does not exist, it will be created.
 */
const getTimer = (timerName: string): Timer => {
  if (!timers[timerName]) {
    timers[timerName] = [];
  }

  return timers[timerName] as Timer;
};

const isTimerFinished = (timer: Timer = []): boolean => {
  return timer.some((step) => step.type === 'finish');
};

const addStep = (
  timer: Timer,
  stepName: string,
  timestamp: number,
  type: TimerStep['type'],
): void => {
  if (isTimerFinished(timer)) {
    return;
  }

  timer.push({ name: stepName, timestamp, type });
};

/**
 * Set an intermediate timer step, call it if your timer has steps.
 */
export const setStep = (
  timerName: string,
  stepName: string,
  stepTimestamp = Date.now(),
): void => {
  addStep(getTimer(timerName), stepName, stepTimestamp, 'step');
};

/**
 * Add the last step, finish the timer.
 */
export const finish = (
  timerName: string,
  stepName = FINISH_STEP,
  finishTimestamp = Date.now(),
): void => {
  addStep(getTimer(timerName), stepName, finishTimestamp, 'finish');
};

export const getDurationOfStep = (timerName: string, step: string): number => {
  const timer = getTimer(timerName);

  const step1 = timer.find(byName(step));
  if (!step1) {
    return 0;
  }

  const step0Index = timer.indexOf(step1) - 1;
  if (-1 === step0Index) {
    return 0;
  }
  const step0 = timer[step0Index] as TimerStep;

  return step1.timestamp - step0.timestamp;
};

/**
 * Receive a timer result in key-value shape (value is in milliseconds),
 * where key is step name and value is how much milliseconds it took from previous step (or start)
 * plus "total" property - total time from start to finish
 */
export const getResult = (timerName: string): TimerResult => {
  const timer = getTimer(timerName);

  if (timer.length <= 1) {
    return { total: 0 };
  }

  const result: TimerResult = { total: 0 };

  if (timer.length > 2) {
    for (const [index, step] of timer.entries()) {
      if (index === 0) {
        continue;
      }

      const prevStep = timer[index - 1] as TimerStep;

      result[`${index}-${step.name}`] = step.timestamp - prevStep.timestamp;
    }
  }

  const firstStep = timer[0] as TimerStep;

  const lastStep = timer[timer.length - 1] as TimerStep;
  result.total = lastStep.timestamp - firstStep.timestamp;

  return result;
};

/**
 * Remove timer data
 */
export const clear = (timerName: string): void => {
  timers[timerName] = undefined;
};
